import React from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import Section from '../components/layout/Section';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import FormInput from '../components/ui/FormInput';
import FormTextarea from '../components/ui/FormTextarea';
import FormSelect from '../components/ui/FormSelect';

const ContactPage: React.FC = () => {
  // Form state
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    interest: ''
  });
  
  // Form errors
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  
  // Success message
  const [showSuccess, setShowSuccess] = React.useState(false);
  
  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Form is valid, show success message
    setShowSuccess(true);
    
    // Reset form after 5 seconds
    setTimeout(() => {
      setShowSuccess(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        interest: ''
      });
    }, 5000);
  };
  
  // Interest options
  const interestOptions = [
    { value: 'buying', label: 'Buying a Property' },
    { value: 'renting', label: 'Renting a Property' },
    { value: 'investing', label: 'Investment Opportunities' },
    { value: 'realtor', label: 'Becoming a Realtor' },
    { value: 'other', label: 'Other Inquiry' }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-grow">
        <div className="bg-primary-dark text-white py-5">
          <div className="container mx-auto px-4 text-center pb-4">
            <h1 className="text-4xl font-bold mb-4">Contact Us</h1>
            <p className="text-xl max-w-2xl mx-auto mb-5">
              Have questions about our properties or services? We're here to help. Reach out to us and our team will get back to you as soon as possible.
            </p>
          </div>
        </div>
        
        <Section background="light">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold text-primary mb-6">Get in Touch</h2>
              
              <Card className="mb-8 p-8">
                <form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormInput 
                      label="Full Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      required
                      error={errors.name}
                    />
                    
                    <FormInput 
                      label="Email Address"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Your email address"
                      required
                      error={errors.email}
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormInput 
                      label="Phone Number"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Your phone number"
                      required
                      error={errors.phone}
                    />
                    
                    <FormSelect 
                      label="Interest"
                      name="interest"
                      value={formData.interest}
                      onChange={handleChange}
                      options={interestOptions}
                      placeholder="Select your interest"
                    />
                  </div>
                  
                  <FormInput 
                    label="Subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="Subject of your message"
                  />
                  
                  <FormTextarea 
                    label="Message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Your message"
                    required
                    error={errors.message}
                    rows={6}
                  />
                  
                  <div className="mt-6">
                    <Button 
                      type="submit"
                      variant="primary"
                      size="lg"
                      className="w-full"
                    >
                      Send Message
                    </Button>
                  </div>
                  
                  {showSuccess && (
                    <div className="mt-4 p-4 bg-green-100 text-green-800 rounded-lg animate-fade-in">
                      Thank you for your message! We will get back to you soon.
                    </div>
                  )}
                </form>
              </Card>
            </div>
            
            <div>
              <h2 className="text-2xl font-bold text-primary mb-6">Contact Information</h2>
              
              <Card className="mb-6 p-6">
                <div className="flex items-start">
                  <div className="text-2xl text-primary mr-4">📍</div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Our Location</h3>
                    <p className="text-gray-700">
                      PLOT 18180 CADESTRAL ZONE<br />
                      B21 SHEGO KARU<br />
                      Abuja, Nigeria
                    </p>
                  </div>
                </div>
              </Card>
              
              <Card className="mb-6 p-6">
                <div className="flex items-start">
                  <div className="text-2xl text-primary mr-4">📞</div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Phone Numbers</h3>
                    <p className="text-gray-700 mb-2">
                      <a href="tel:+2348039652021" className="hover:text-primary transition-colors">+234 803 965 2021</a> (Mohammed)
                    </p>
                    <p className="text-gray-700">
                      <a href="tel:+2348036750694" className="hover:text-primary transition-colors">
                        +234 904 196 2781</a> (Henry)
                    </p>
                  </div>
                </div>
              </Card>
              
              <Card className="mb-6 p-6">
                <div className="flex items-start">
                  <div className="text-2xl text-primary mr-4">📧</div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Email Address</h3>
                    <p className="text-gray-700">
                      <a href="mailto:info@uniquesolargardens.com" className="hover:text-primary transition-colors">info@uniquesolargardens.com</a>
                    </p>
                  </div>
                </div>
              </Card>
              
              <Card className="p-6">
                <div className="flex items-start">
                  <div className="text-2xl text-primary mr-4">⏰</div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Business Hours</h3>
                    <p className="text-gray-700 mb-1">Monday - Friday: 8:00 AM - 6:00 PM</p>
                    <p className="text-gray-700 mb-1">Saturday: 9:00 AM - 4:00 PM</p>
                    <p className="text-gray-700">Sunday: Closed</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </Section>
        
        <Section background="white">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-primary mb-4">Our Location</h2>
            <p className="text-gray-700 max-w-3xl mx-auto">
              Visit our estate to experience the beauty and tranquility of Unique Solar Gardens. We're conveniently located in SHEGO District, KARU, with easy access to major roads and amenities.
            </p>
          </div>
          
          <div className="rounded-lg overflow-hidden shadow-card h-96">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m16!1m12!1m3!1d7881.567896404702!2d7.567007684259175!3d8.992018061301492!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!2m1!1sPLOT%2018180%20CADESTRAL%20ZONE%20B21%20SHEGO%20KARU%20Abuja%2C%20Nigeria!5e0!3m2!1sen!2sng!4v1750101330487!5m2!1sen!2sng"
              width="100%"
              height="100%"
              className="w-full h-full"
              allowFullScreen={true}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Unique Solar Gardens Estate Location"
            ></iframe>
          </div>
        </Section>
      </main>
      
      <Footer />
    </div>
  );
};

export default ContactPage;
